import { z } from 'zod';
const looseEventSchema = z.unknown();
export const queueRequestKindSchema = z.enum(['standard', 'inbound_hearing']);
export const queueMessageSchema = z.object({
    clientId: z.string().optional(),
    data: z.unknown().optional(),
    name: z.string().optional(),
    role: z.string().optional(),
    token: z.string().optional(),
    type: z.string(),
    userId: z.string().optional(),
    deviceId: z.string().optional()
}).passthrough();
export const interpreterInfoSchema = z.object({
    id: z.string(),
    languages: z.array(z.string()),
    name: z.string(),
    status: z.enum(['active', 'busy', 'inactive'])
}).passthrough();
/**
 * The client's own Zoom/Meet/Teams/Webex meeting ("Interpret my meeting",
 * vrs-server/lib/external-meeting-vrs.js). Offers and queue views carry
 * platform + host; url (it can hold a passcode) only reaches the interpreter
 * and client of the committed call.
 */
export const externalMeetingSchema = z.object({
    host: z.string().optional(),
    platform: z.enum(['zoom', 'meet', 'teams', 'webex']).nullable().optional(),
    url: z.string().optional()
}).passthrough();
export const requestInfoSchema = z.object({
    callerIdPhone: z.string().optional().nullable(),
    callType: z.enum(['vrs', 'vri']).optional(),
    corporateAccountId: z.string().optional().nullable(),
    costCenter: z.string().optional().nullable(),
    department: z.string().optional().nullable(),
    industryTag: z.string().optional().nullable(),
    instructions: z.string().optional().nullable(),
    clientName: z.string(),
    id: z.string(),
    language: z.string(),
    position: z.number().optional(),
    requestKind: queueRequestKindSchema.optional(),
    roomName: z.string().optional(),
    scheduledSessionId: z.string().optional().nullable(),
    scheduledStartAt: z.string().optional().nullable(),
    targetPhone: z.string().optional().nullable(),
    targetPhoneLast4: z.string().regex(/^\d{4}$/).optional(),
    timestamp: z.number().optional(),
    vcoEnabled: z.boolean().optional(),
    externalMeeting: externalMeetingSchema.optional(),
    /** Server-owned: the client is a Demo account, so the call is not payable. */
    demoMode: z.boolean().optional(),
    /**
     * On a client's own queue_status entries only: whether the receiving
     * browser placed this request (a reloaded portal takes it back). Absent
     * when the server does not know which browser asked.
     */
    forThisDevice: z.boolean().optional()
}).passthrough();
export const queueMatchPayloadSchema = z.object({
    callerIdPhone: z.string().optional().nullable(),
    callId: z.string().optional(),
    callType: z.enum(['vrs', 'vri']).optional(),
    clientId: z.string().optional(),
    clientName: z.string().optional(),
    clientStartWithVideoMuted: z.boolean().optional(),
    durationMinutes: z.number().optional(),
    endedBy: z.string().optional(),
    // Display name of endedBy when the host ended a group call for everyone
    // (reason 'ended_for_all'): "Dana ended the call for everyone."
    endedByName: z.string().optional(),
    // Set on the call_ended a group participant receives for their own
    // departure: the call goes on for everyone else.
    participantId: z.string().optional(),
    endedByRole: z.string().optional(),
    endReason: z.string().optional(),
    interpreterId: z.string().optional(),
    interpreterName: z.string().optional(),
    language: z.string().optional(),
    reason: z.string().optional(),
    requestKind: queueRequestKindSchema.optional(),
    requestId: z.string().optional(),
    roomName: z.string().optional(),
    // Which mechanism ended the call: 'participant_call_end', or the guard's
    // own name ('client_no_show_guard', 'stale_call_sweeper', …). endedByRole
    // alone says 'system' for six different terminators, which is what made
    // the 2026-08-15 teardown unattributable without server log access.
    surface: z.string().optional(),
    targetPhone: z.string().optional().nullable(),
    targetPhoneLast4: z.string().regex(/^\d{4}$/).optional(),
    vcoEnabled: z.boolean().optional(),
    externalMeeting: externalMeetingSchema.optional(),
    /** Epoch ms (server clock) the client's request was created (match messages). */
    requestCreatedAt: z.number().optional(),
    /** Epoch ms (server clock) the interpreter's accept was committed (match messages). */
    matchedAt: z.number().optional(),
    /**
     * This recipient's own Jitsi room token, signed at Accept with the rules of
     * POST /api/auth/jitsi-token. Absent for inbound hearing and external
     * meetings, when signing failed, or when the client's socket was on another
     * instance: mint one then. Never stored or logged.
     */
    roomToken: z.string().optional(),
    /**
     * On the client's queue match (match_found/meeting_initiated): true only
     * on the copy sent to the browser that placed the request, which enters
     * the room even if it lost its own record of the request; false on any
     * other browser of the account, which rings. Absent when the server does
     * not know which browser asked, on fan-out through another instance, and
     * on re-rings.
     */
    forThisDevice: z.boolean().optional()
}).passthrough();
export const interpreterRequestPayloadSchema = z.object({
    callerIdPhone: z.string().optional().nullable(),
    callType: z.enum(['vrs', 'vri']).optional(),
    corporateAccountId: z.string().optional().nullable(),
    costCenter: z.string().optional().nullable(),
    department: z.string().optional().nullable(),
    industryTag: z.string().optional().nullable(),
    instructions: z.string().optional().nullable(),
    clientName: z.string(),
    id: z.string(),
    language: z.string(),
    /** UUID of the current durable offer generation; absent on older servers/P2P. */
    offerId: z.string().uuid().optional(),
    /** ISO time the queue withdraws this offer (server QUEUE_OFFER_WINDOW_MS). */
    offerExpiresAt: z.string().optional(),
    /** Epoch ms (server clock) this offer generation was made; redeliveries keep it. */
    offeredAt: z.number().optional(),
    requestKind: queueRequestKindSchema.optional(),
    roomName: z.string().optional(),
    scheduledSessionId: z.string().optional().nullable(),
    scheduledStartAt: z.string().optional().nullable(),
    targetPhone: z.string().optional().nullable(),
    targetPhoneLast4: z.string().regex(/^\d{4}$/).optional(),
    timestamp: z.number().optional(),
    vcoEnabled: z.boolean().optional(),
    externalMeeting: externalMeetingSchema.optional(),
    /** Server-owned: the client is a Demo account, so the call is not payable. */
    demoMode: z.boolean().optional()
}).passthrough();
export const queueErrorPayloadSchema = z.object({
    /**
     * What was refused, e.g. 'accept_request', 'prepare_vri_invite', or
     * 'request_interpreter' (every refusal of the request itself, including a
     * payload validation_error): only that one ends a waiting request.
     */
    operation: z.string().optional(),
    requestId: z.string().optional(),
    roomName: z.string().optional(),
    code: z.string().optional(),
    message: z.string().optional(),
    retrying: z.boolean().optional(),
    // conference_invite_cancel refusals name the ring they were about.
    targetId: z.string().nullable().optional()
}).passthrough();
/*
 * An interpreter calling a client, rather than waiting for one to arrive.
 *
 * The mirror of requestInterpreter. Room entry is not carried by these: the
 * server follows the started event with `meeting_initiated`, so the ordinary
 * match path takes the interpreter into the call. These two say what to show
 * while that happens, and what to say when the client cannot be reached —
 * busy, do-not-disturb and unreachable all fail closed server-side.
 */
export const interpreterClientCallStartedPayloadSchema = z.object({
    callId: z.string().optional(),
    clientName: z.string().optional(),
    roomName: z.string().optional()
}).passthrough();
export const interpreterClientCallFailedPayloadSchema = z.object({
    code: z.string().optional(),
    message: z.string().optional()
}).passthrough();
export const vriInvitePreparedPayloadSchema = z.object({
    requestId: z.string().optional(),
    expiresAt: z.string().optional(),
    roomName: z.string().optional(),
    status: z.enum(['prepared', 'live']).optional(),
    deliveries: z.array(z.object({
        channel: z.enum(['email', 'sms']),
        errorCode: z.string().nullable().optional(),
        status: z.enum(['sent', 'blocked', 'failed']),
        targetRef: z.string()
    }).passthrough()).optional(),
    inviteUrl: z.string().optional(),
    token: z.string().optional()
}).passthrough();
export const voicemailEventPayloadSchema = z.object({
    calleeId: z.string().optional(),
    calleeName: z.string().optional(),
    calleePhone: z.string().optional(),
    count: z.number().optional(),
    durationSeconds: z.number().optional(),
    maxDurationSeconds: z.number().optional(),
    message: z.string().optional(),
    messageId: z.string().optional(),
    roomName: z.string().optional(),
    voicemailAvailable: z.boolean().optional()
}).passthrough();
export const notificationEventPayloadSchema = z.object({
    body: z.string().optional(),
    data: z.record(z.string(), z.unknown()).optional(),
    priority: z.enum(['normal', 'high']).optional(),
    title: z.string().optional(),
    type: z.string().optional()
}).passthrough();
const vrsClientReachabilitySchema = z.object({
    calleeId: z.string().optional(),
    calleeName: z.string().optional(),
    retryAfterMs: z.number().optional(),
    voicemailAvailable: z.boolean().optional()
}).passthrough();
export const queueStatusSchema = z.object({
    activeInterpreters: z.array(interpreterInfoSchema),
    paused: z.boolean().optional(),
    pendingRequests: z.array(requestInfoSchema),
    // Interpreters only: waiting requests they would be offered once
    // Available that no other interpreter holds an offer for (eligibleQueueSize
    // in vrs-server/lib/state.js). Absent until the server knows. It ends the
    // post-call breather early (wrapUpActions.ts); queueSize never does.
    eligibleQueueSize: z.number().int().nonnegative().optional(),
    // Waiting requests in the recipient's tenant and service region
    // (scopedQueueCounts in vrs-server/lib/state.js): every language, service
    // mode and held offer. Interpreters get counts only.
    queueSize: z.number().int().nonnegative().optional(),
    totalMatches: z.number()
}).passthrough();
const p2pFailureSchema = z.object({
    message: z.string().optional(),
    // A group call nobody could be found for: what was entered for each.
    unresolved: z.array(z.unknown()).optional()
}).passthrough();
export const p2pIncomingSchema = queueMatchPayloadSchema.extend({
    invitationId: z.string().optional(),
    conferenceInvitation: z.boolean().optional(),
    expiresAt: z.string().datetime().optional(),
    callerId: z.string().optional(),
    callerName: z.string().optional(),
    calleeId: z.string().optional(),
    groupCall: z.boolean().optional(),
    inboundVrs: z.boolean().optional(),
    participantCount: z.number().optional()
}).passthrough();
export const p2pTargetSchema = z.object({
    availability: z.object({
        dnd: z.number().optional(),
        offline: z.number().optional(),
        requested: z.number().optional(),
        resolved: z.number().optional(),
        unresolved: z.number().optional()
    }).optional(),
    callId: z.string().optional(),
    calleeId: z.string().optional(),
    calleeName: z.string().nullable().optional(),
    calleePhone: z.string().optional(),
    deliveryReason: z.string().optional(),
    groupCall: z.boolean().optional(),
    participantCount: z.number().optional(),
    reason: z.string().optional(),
    // A group call: the entries that matched no account (as typed).
    unresolved: z.array(z.unknown()).optional(),
    voicemailAvailable: z.boolean().optional()
}).passthrough();
/*
 * Deaf-to-deaf group calls (2026-09-24). Any joined participant may ring
 * someone else in with conference_add; the server answers the inviter with
 * conference_invite_sent (or an error carrying operation 'conference_add')
 * and later conference_invite_result. Everyone in the call hears who joined
 * and who left; only a real call_ended ends the room.
 */
export const conferenceInviteSentSchema = z.object({
    callId: z.string().optional(),
    expiresAt: z.string().optional(),
    inviteId: z.string().optional(),
    // What was asked for, as sent: the handle, email, number or clientId.
    requested: z.string().nullable().optional(),
    // True when the server rang the person's devices.
    ringing: z.boolean().optional(),
    // Who it resolved to: { clientId, name }.
    target: z.unknown().optional()
}).passthrough();
/**
 * How an invitation's ring ended. 'cancelled': the inviter or the host
 * stopped it (conference_invite_cancel { callId, targetId }); answered by
 * this, or by error { code, operation: 'conference_invite_cancel', callId,
 * targetId } with NOT_INVITER, NOT_RINGING, NOT_PARTICIPANT, CALL_ENDED,
 * NOT_FOUND, AUTH_REQUIRED or INTERNAL_ERROR.
 */
export const conferenceInviteStatusSchema = z.enum(['accepted', 'declined', 'missed', 'cancelled']);
export const conferenceInviteResultSchema = z.object({
    callId: z.string().optional(),
    inviteId: z.string().optional(),
    name: z.string().nullable().optional(),
    status: conferenceInviteStatusSchema.optional(),
    targetId: z.string().optional()
}).passthrough();
export const p2pParticipantPresenceSchema = z.object({
    callId: z.string().optional(),
    name: z.string().optional(),
    participantCount: z.number().optional(),
    // joined: came back after leaving; left: taken out by another member.
    rejoined: z.boolean().optional(),
    removed: z.boolean().optional(),
    userId: z.string().optional()
}).passthrough();
const callPeerListSchema = z.array(z.object({ id: z.string().optional(), name: z.string().nullable().optional() }).passthrough());
/**
 * Why call_ended closed a deaf-to-deaf group call from its host's hang-up
 * menu: 'ended_for_all' (p2p_end_for_all: everyone in it or ringing for it
 * hears it, with endedBy/endedByName) or 'already_ended' (the host's repeat
 * of an end that already happened). Other call_ended reasons are free text.
 */
export const groupCallEndReasonSchema = z.enum(['ended_for_all', 'already_ended']);
/** Nobody answered a group call the caller is waiting in (p2p_group_no_answer). */
export const p2pGroupNoAnswerSchema = z.object({
    callId: z.string().optional(),
    declined: callPeerListSchema.optional(),
    missed: callPeerListSchema.optional(),
    roomName: z.string().optional()
}).passthrough();
/**
 * A ring this client missed while the call is still live
 * (p2p_room_still_active). A group invitee may still join it by accepting
 * again: p2p_accept { callId, callerId, roomName }, answered by p2p_join_room.
 */
export const p2pRoomStillActiveSchema = z.object({
    callId: z.string().optional(),
    callerId: z.string().optional(),
    callerName: z.string().optional(),
    groupCall: z.boolean().optional(),
    participantCount: z.number().optional(),
    roomName: z.string().optional()
}).passthrough();
/**
 * One live call per account across devices (vrs-server/lib/call-holders.js).
 * A second device is never silently let into, or handed, a call another
 * device of the same account is carrying: it is told which device holds the
 * call (a display label such as "Chrome on Mac") and since when.
 * Interpreters wait (session_held_elsewhere); a client may move the call.
 */
export const accountCallHoldSchema = z.object({
    role: z.enum(['client', 'interpreter']).optional(),
    callId: z.string().optional(),
    roomName: z.string().nullable().optional(),
    device: z.string().optional(),
    since: z.string().nullable().optional(),
    canMoveHere: z.boolean().optional(),
    recheckAfterMs: z.number().optional(),
    message: z.string().optional()
}).passthrough();
export const accountCallStatusSchema = accountCallHoldSchema.extend({
    held: z.boolean()
}).passthrough();
/** call_moving_elsewhere / call_moved_elsewhere / call_move_lapsed. */
export const callMoveNoticeSchema = z.object({
    callId: z.string().optional(),
    roomName: z.string().nullable().optional(),
    device: z.string().optional(),
    message: z.string().optional()
}).passthrough();
export const callEndIgnoredSchema = z.object({
    callId: z.string().optional(),
    reason: z.string().optional(),
    message: z.string().optional()
}).passthrough();
/**
 * Why request_withdrawn took an interpreter's offer card down (vrs-server
 * lib/queue-service.js, ws/queue-notify.js, ws/queue-handlers.js). The shared
 * queue service emits request_withdrawn as requestCancelled, so that event
 * carries these as well as request_cancelled's own reasons. 'queue_paused':
 * an admin paused the queue for the request's organization or the platform;
 * the request stays waiting and is offered again on resume, so it is neither
 * a decline nor an expiry. 'offer_released' and 'queue_paused' name the offer
 * generation they end (offerId).
 */
export const requestWithdrawnReasonSchema = z.enum([
    'assigned',
    'cancelled',
    'declined',
    'offer_expired',
    'offer_released',
    'queue_paused',
    'removed'
]);
export const queueEventSchemas = {
    authenticated: z.object({
        clientId: z.string().optional(),
        role: z.string().optional(),
        // Server-decided features (vrs-server/ws/auth-handlers.js).
        capabilities: z.object({
            calendarReminders: z.boolean().optional(),
            externalMeetingVrs: z.boolean().optional()
        }).passthrough().optional()
    }).passthrough(),
    callHoldUpdated: looseEventSchema,
    callOffHold: looseEventSchema,
    callOnHold: looseEventSchema,
    callTransferAccepted: looseEventSchema,
    callTransferCancelled: looseEventSchema,
    callTransferInitiated: looseEventSchema,
    callTransferPending: looseEventSchema,
    callWaitingIncoming: looseEventSchema,
    callWaitingResponded: looseEventSchema,
    chatHistory: looseEventSchema,
    chatMessage: looseEventSchema,
    chatMessageSent: looseEventSchema,
    callEnded: queueMatchPayloadSchema,
    clientReringSent: looseEventSchema,
    clientReringSkipped: looseEventSchema,
    conferenceAddOffline: looseEventSchema,
    conferenceAddRinging: looseEventSchema,
    conferenceInvite: looseEventSchema,
    conferenceInviteResult: conferenceInviteResultSchema,
    conferenceInviteSent: conferenceInviteSentSchema,
    conferenceInvitationError: z.object({ invitationId: z.string().optional(), callId: z.string().optional(), code: z.string().optional(), message: z.string().optional() }),
    // 'expired': the ring is over without an answer (it ran out, its inviter
    // cancelled it, or the host ended the call for everyone).
    conferenceInvitationUpdate: z.object({ invitationId: z.string().optional(), callId: z.string().optional(), participantId: z.string().optional(), status: z.enum(['accepted', 'declined', 'unavailable', 'expired']).optional() }),
    conferenceParticipantRemoved: looseEventSchema,
    conferenceRemoved: looseEventSchema,
    connection: z.object({
        connected: z.boolean(),
        maxAttemptsReached: z.boolean().optional(),
        message: z.string().optional()
    }).passthrough(),
    contactsChanged: looseEventSchema,
    error: queueErrorPayloadSchema,
    handoff_complete: looseEventSchema,
    handoff_consumed: looseEventSchema,
    handoff_cancelled: looseEventSchema,
    handoff_error: looseEventSchema,
    handoff_executed: looseEventSchema,
    handoff_devices_updated: looseEventSchema,
    handoff_in_progress: looseEventSchema,
    handoff_offer: looseEventSchema,
    handoff_prepared: looseEventSchema,
    handoff_release: looseEventSchema,
    inboundVrsCall: looseEventSchema,
    interpreterRequest: interpreterRequestPayloadSchema,
    matchFound: queueMatchPayloadSchema,
    meetingInitiated: queueMatchPayloadSchema,
    // Calendar meetings that are starting (phase 2); ids and platforms, never links.
    meetingReminder: z.object({ reminders: z.array(z.unknown()).optional() }).passthrough(),
    // The interpreter entered / identified the client in their own meeting.
    externalMeetingProgress: z.object({
        callId: z.string().optional(),
        event: z.enum(['entered', 'identified']).optional(),
        platform: z.string().nullable().optional()
    }).passthrough(),
    notificationEvent: notificationEventPayloadSchema,
    p2pCallFailed: p2pFailureSchema,
    p2pIncomingCall: p2pIncomingSchema,
    p2pAccepted: p2pIncomingSchema,
    p2pCallCancelled: looseEventSchema,
    p2pCallEnded: p2pIncomingSchema,
    p2pDeclined: p2pIncomingSchema,
    // Nobody answered a group call the caller is still waiting on.
    p2pGroupNoAnswer: p2pGroupNoAnswerSchema,
    p2pJoinRoom: p2pIncomingSchema,
    p2pParticipantJoined: p2pParticipantPresenceSchema,
    p2pParticipantLeft: p2pParticipantPresenceSchema,
    // In-call "call an interpreter into this P2P room" (green button).
    p2pInterpreterRequested: z.object({
        callId: z.string(),
        requestId: z.string().optional()
    }).passthrough(),
    p2pInterpreterJoined: z.object({
        callId: z.string().optional(),
        interpreterName: z.string().optional(),
        roomName: z.string().optional()
    }).passthrough(),
    p2pInterpreterUnavailable: z.object({
        callId: z.string().optional()
    }).passthrough(),
    p2pRinging: queueMatchPayloadSchema.extend({
        calleeId: z.string().optional(),
        calleeName: z.string().optional(),
        // 'socket' — the callee's app is open; 'push' — their device was
        // woken; 'unconfirmed' — ringing on hope (no device could be
        // alerted) while the invite waits for them to connect.
        delivery: z.enum(['socket', 'push', 'unconfirmed']).optional(),
        deliveryReason: z.string().optional(),
        groupCall: z.boolean().optional(),
        participantCount: z.number().optional(),
        // A group call's first round: who is being rung, who could not be
        // alerted yet (still rung, invite held for reconnect), and who has
        // Do Not Disturb on (not rung, no seat).
        invited: callPeerListSchema.optional(),
        offline: callPeerListSchema.optional(),
        dnd: callPeerListSchema.optional(),
        // Group invitees the server could not reach, as entered (unknown,
        // private or blocked handles look alike on purpose).
        unresolved: z.array(z.unknown()).optional()
    }).passthrough(),
    p2pRoomStillActive: p2pRoomStillActiveSchema,
    p2pTargetDnd: p2pTargetSchema,
    p2pTargetOffline: p2pTargetSchema,
    vrsClientUnavailable: p2pTargetSchema,
    vrsClientReconnecting: vrsClientReachabilitySchema,
    p2p_target_offline: p2pTargetSchema,
    preferencesUpdated: looseEventSchema,
    queueStatus: queueStatusSchema,
    requestAccepted: queueMatchPayloadSchema,
    requestAssigned: queueMatchPayloadSchema,
    requestCancelled: z.object({
        /** The offer generation a withdrawal ends; a card showing a newer one keeps it. */
        offerId: z.string().optional(),
        reason: z.union([z.enum(['timeout', 'removed_by_admin']), requestWithdrawnReasonSchema]).optional(),
        requestId: z.string().optional(),
        success: z.boolean().optional(),
        timedOut: z.boolean().optional()
    }).passthrough(),
    requestDeclined: queueMatchPayloadSchema,
    requestQueued: z.object({
        position: z.number().optional(),
        requestId: z.string().optional(),
        /** Epoch ms (server clock) the request was created; a reused request keeps its time. */
        queuedAt: z.number().optional()
    }).passthrough(),
    session_registered: looseEventSchema,
    session_unregistered: looseEventSchema,
    adminForceLogout: z.object({ reason: z.string().optional(), message: z.string().optional() }).passthrough(),
    sessionSuperseded: z.object({ code: z.string().optional(), message: z.string().optional() }).passthrough(),
    // One live call per account: this device is held out / the call is on
    // (or moving to, or moved to) another device of the same account.
    sessionHeldElsewhere: accountCallHoldSchema,
    accountCallStatus: accountCallStatusSchema,
    callActiveElsewhere: accountCallHoldSchema,
    callMovingElsewhere: callMoveNoticeSchema,
    callMovedElsewhere: callMoveNoticeSchema,
    callMoveLapsed: callMoveNoticeSchema,
    callEndIgnored: callEndIgnoredSchema,
    // The delayed half of a kick: the server already destroyed the session, so
    // the next reconnect is rejected with SESSION_REVOKED and refresh cannot
    // recover it. Distinct from adminForceLogout, which arrives live.
    interpreterClientCallFailed: interpreterClientCallFailedPayloadSchema,
    interpreterClientCallStarted: interpreterClientCallStartedPayloadSchema,
    sessionRevoked: z.object({ code: z.string().optional(), message: z.string().optional() }).passthrough(),
    statusUpdated: z.object({ status: z.string().optional() }).passthrough(),
    voicemail_error: voicemailEventPayloadSchema,
    voicemail_message_deleted: voicemailEventPayloadSchema,
    voicemail_new_message: voicemailEventPayloadSchema,
    voicemail_recording_cancelled: voicemailEventPayloadSchema,
    voicemail_recording_complete: voicemailEventPayloadSchema,
    voicemail_recording_started: voicemailEventPayloadSchema,
    voicemail_unread_count: voicemailEventPayloadSchema,
    vriInvitePrepared: vriInvitePreparedPayloadSchema
};
export const queueSequenceContracts = {
    requestInterpreter: [
        'connection',
        'requestQueued',
        'interpreterRequest',
        'requestAccepted',
        'matchFound',
        'meetingInitiated'
    ],
    interpreterAvailability: [
        'connection',
        'authenticated',
        'statusUpdated',
        'queueStatus'
    ],
    vriInvitePreparation: [
        'connection',
        'vriInvitePrepared'
    ],
    interpreterCallsClient: [
        'connection',
        'authenticated',
        'interpreterClientCallStarted',
        'meetingInitiated'
    ]
};
export const queueEventNames = Object.keys(queueEventSchemas);
